import { useCallback, useMemo, useState } from 'react'

import { ComboboxMulti, DefaultPopover, Label } from 'twyne-new.ui'

import { useAppAlert } from '@Components/AppAlert'

import { isReportPinned } from '../utils/reportViewConfig'
import { useCustomReportStore } from '../store'

export const PIN_BUTTON_ID = 'custom-report-pin-btn'

export default function ReportPinDropdown({ open, onOpenChange }) {
  const { handleError } = useAppAlert()
  const {
    views: { items },
    activeView: { setPinned },
    report: { isLoading },
  } = useCustomReportStore()
  const [updating, setUpdating] = useState(false)

  const options = useMemo(
    () => items.map(item => ({
      value: String(item.id),
      label: item.description,
    })),
    [items],
  )

  const pinnedIds = useMemo(
    () => items.filter(item => isReportPinned(item.configuration)).map(item => String(item.id)),
    [items],
  )

  const handleChange = useCallback(async selectedIds => {
    const updates = items.filter(item => {
      const shouldPin = selectedIds.includes(String(item.id))
      return shouldPin !== isReportPinned(item.configuration)
    })

    if (!updates.length) return

    setUpdating(true)
    try {
      for (const item of updates) {
        await setPinned(item.id, selectedIds.includes(String(item.id)))
      }
    } catch {
      handleError('Error', 'Sidebar reports were not updated.')
    } finally {
      setUpdating(false)
    }
  }, [items, setPinned, handleError])

  return (
    <DefaultPopover
      target={`#${PIN_BUTTON_ID}`}
      wrapperProps={{ open, onOpenChange }}
      triggerProps={{ asChild: true }}
      contentProps={{
        className: 'w-72 p-3',
        align: 'center',
        side: 'right',
        sideOffset: 8,
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label>Pinned reports</Label>
        <ComboboxMulti
          options={options}
          value={pinnedIds}
          onChange={handleChange}
          placeholder="Select the pinned reports"
          hideSelectAll
          maxCount={2}
          disabled={updating || isLoading || !options.length}
        />
      </div>
    </DefaultPopover>
  )
}
